import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getGalleriesByCategory } from '../services/api';
import { PageLayout } from './layouts/PageLayout';
import { Navigation } from './Navigation';
import { Footer } from './Footer';
import { GalleryGrid } from './gallery/GalleryGrid';
import { Lightbox } from './gallery/Lightbox';

const CATEGORY_INFO = {
  main: { title: 'Main Gallery', color: 'orange' },
  art: { title: 'Art Gallery', color: 'green' },
  party: { title: 'Party Gallery', color: 'purple' },
};

const COLOR_CLASSES = { orange: 'from-orange-500', green: 'from-green-500', purple: 'from-purple-500' };

const GalleryView = () => {
  const { category } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);

  const info = CATEGORY_INFO[category] || CATEGORY_INFO.main;

  useEffect(() => {
    const fetchGallery = async () => {
      setLoading(true);
      try { setItems((await getGalleriesByCategory(category)).data); }
      catch (error) { console.error('Error fetching gallery:', error); }
      setLoading(false);
    };
    fetchGallery();
  }, [category]);

  useEffect(() => {
    document.body.style.overflow = selectedIndex !== null ? 'hidden' : 'auto';
    return () => { document.body.style.overflow = 'auto'; };
  }, [selectedIndex]);

  const goToPrevious = () => setSelectedIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  const goToNext = () => setSelectedIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));

  return (
    <>
      <Navigation />
      <PageLayout>
        <div className="min-h-screen pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-12">
              <Link to="/gallery" className="inline-flex items-center gap-2 text-gray-400 hover:text-orange-500 transition-all duration-300 mb-6 group">
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-300" />
                <span className="group-hover:tracking-wide transition-all duration-300">Back to Galleries</span>
              </Link>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold group cursor-default">
                <span className={`text-transparent bg-clip-text bg-gradient-to-r ${COLOR_CLASSES[info.color]} to-white transition-all duration-300 group-hover:tracking-wider`}>
                  {info.title}
                </span>
              </h1>
            </div>

            {loading ? (
              <div className="text-center text-gray-400 py-20">Loading gallery...</div>
            ) : (
              <GalleryGrid items={items} onItemClick={setSelectedIndex} />
            )}
          </div>
        </div>

        <Lightbox items={items} selectedIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)} onPrevious={goToPrevious} onNext={goToNext} />
      </PageLayout>
      <Footer />
    </>
  );
};

export default GalleryView;
